// src/pages/Appointments/Admin/VisitTracking/CompleteVisitModal.jsx
import React, { useState } from "react";
import {
  FiCheckCircle,
  FiShoppingBag,
  FiX,
  FiDollarSign,
  FiAlertCircle,
} from "react-icons/fi";
import Button from "../../../../components/common/Button";

const CompleteVisitModal = ({ appointment, onClose, onComplete, loading }) => {
  const [visitData, setVisitData] = useState({
    converted: false,
    conversionValue: "",
    orderId: "",
    noPurchaseReason: "",
    noPurchaseDetails: "",
    futureIntent: {
      willingToBuy: false,
      estimatedTimeframe: "",
    },
    adminNotes: "",
  });

  const noPurchaseReasons = [
    { value: "PRICE_TOO_HIGH", label: "Price Too High" },
    { value: "PRODUCT_NOT_FOUND", label: "Product Not Found" },
    { value: "NEEDS_MORE_TIME", label: "Needs More Time to Decide" },
    { value: "COMPARING_OPTIONS", label: "Comparing with Other Options" },
    { value: "WAITING_FOR_OCCASION", label: "Waiting for Right Occasion" },
    { value: "BUDGET_CONSTRAINT", label: "Budget Constraint" },
    { value: "JUST_BROWSING", label: "Just Browsing" },
    { value: "QUALITY_CONCERN", label: "Quality Concern" },
    { value: "OTHER", label: "Other" },
  ];

  const handleSubmit = () => {
    // Validation
    if (visitData.converted) {
      if (!visitData.orderId || !visitData.conversionValue) {
        alert("Please provide Order ID and Purchase Value");
        return;
      }
    }

    onComplete(visitData);
  };

  // Calculate visit duration
  const getVisitDuration = () => {
    if (appointment.visit?.checkInTime) {
      const duration = Math.floor(
        (new Date() - new Date(appointment.visit.checkInTime)) / 60000
      );
      return duration;
    }
    return appointment.duration || 30;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <FiCheckCircle className="text-green-600 text-2xl" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-text">Complete Visit</h3>
                <p className="text-sm text-neutral-dark">
                  Record visit outcome and checkout customer
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="p-2 hover:bg-neutral-light rounded-full transition-colors"
            >
              <FiX className="text-xl text-neutral-dark" />
            </button>
          </div>
        </div>

        {/* Visit Summary */}
        <div className="p-6 bg-blue-50 border-b">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm text-neutral-dark">Visit Duration</p>
              <p className="text-2xl font-bold text-primary">
                {getVisitDuration()} min
              </p>
            </div>
            <div>
              <p className="text-sm text-neutral-dark">Products Viewed</p>
              <p className="text-2xl font-bold text-blue-600">
                {appointment.visit?.activities?.productsViewed?.length || 0}
              </p>
            </div>
            <div>
              <p className="text-sm text-neutral-dark">Products Tried</p>
              <p className="text-2xl font-bold text-purple-600">
                {appointment.visit?.activities?.productsTriedOn?.length || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Conversion Status */}
          <div className="bg-neutral-light rounded-lg p-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={visitData.converted}
                onChange={(e) =>
                  setVisitData({ ...visitData, converted: e.target.checked })
                }
                className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <div className="ml-3">
                <span className="text-lg font-semibold text-text">
                  Customer Made a Purchase
                </span>
                <p className="text-sm text-neutral-dark">
                  Check this if the customer bought something
                </p>
              </div>
            </label>
          </div>

          {/* Purchase Details */}
          {visitData.converted ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-600 mb-4">
                <FiShoppingBag className="text-xl" />
                <h4 className="font-semibold text-lg">Purchase Details</h4>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order ID *
                  </label>
                  <input
                    type="text"
                    value={visitData.orderId}
                    onChange={(e) =>
                      setVisitData({ ...visitData, orderId: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter order ID"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purchase Value (₹) *
                  </label>
                  <input
                    type="number"
                    value={visitData.conversionValue}
                    onChange={(e) =>
                      setVisitData({
                        ...visitData,
                        conversionValue: e.target.value,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="0"
                    required
                  />
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FiCheckCircle className="text-green-600" />
                  <span className="font-medium text-green-800">
                    Successful Conversion
                  </span>
                </div>
                <p className="text-sm text-green-700">
                  Order will be linked to this appointment and customer's
                  purchase history will be updated.
                </p>
              </div>
            </div>
          ) : (
            /* No Purchase Section */
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-orange-600 mb-4">
                <FiAlertCircle className="text-xl" />
                <h4 className="font-semibold text-lg">No Purchase Made</h4>
              </div>

              {/* Reason Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for No Purchase
                </label>
                <select
                  value={visitData.noPurchaseReason}
                  onChange={(e) =>
                    setVisitData({
                      ...visitData,
                      noPurchaseReason: e.target.value,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select reason</option>
                  {noPurchaseReasons.map((reason) => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Additional Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Details
                </label>
                <textarea
                  value={visitData.noPurchaseDetails}
                  onChange={(e) =>
                    setVisitData({
                      ...visitData,
                      noPurchaseDetails: e.target.value,
                    })
                  }
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Provide more details about why customer didn't purchase..."
                />
              </div>

              {/* Future Intent */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="flex items-center cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={visitData.futureIntent.willingToBuy}
                    onChange={(e) =>
                      setVisitData({
                        ...visitData,
                        futureIntent: {
                          ...visitData.futureIntent,
                          willingToBuy: e.target.checked,
                        },
                      })
                    }
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 font-medium text-text">
                    Customer Interested in Future Purchase
                  </span>
                </label>

                {visitData.futureIntent.willingToBuy && (
                  <input
                    type="text"
                    value={visitData.futureIntent.estimatedTimeframe}
                    onChange={(e) =>
                      setVisitData({
                        ...visitData,
                        futureIntent: {
                          ...visitData.futureIntent,
                          estimatedTimeframe: e.target.value,
                        },
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Estimated timeframe (e.g., 1 week, 2 months)"
                  />
                )}
              </div>
            </div>
          )}

          {/* Admin Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visit Summary & Notes
            </label>
            <textarea
              value={visitData.adminNotes}
              onChange={(e) =>
                setVisitData({ ...visitData, adminNotes: e.target.value })
              }
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Summarize the visit, customer behavior, preferences, and any follow-up actions needed..."
            />
          </div>

          {/* Checkout Time */}
          <div className="bg-neutral-light rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-dark">Checkout Time</p>
                <p className="text-lg font-semibold text-text">
                  {new Date().toLocaleString("en-IN", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-dark">Total Duration</p>
                <p className="text-lg font-semibold text-primary">
                  {getVisitDuration()} minutes
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t bg-neutral-light flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={loading}
            disabled={
              loading ||
              (visitData.converted &&
                (!visitData.orderId || !visitData.conversionValue))
            }
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            <FiCheckCircle className="mr-2" />
            {loading ? "Completing..." : "Complete Visit & Checkout"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CompleteVisitModal;
